import React from 'react';
import PropTypes from 'prop-types';
import { FormHelperText, MenuItem, TextField } from '@material-ui/core';
import { Autocomplete } from '@knockrentals/knock-shared-web';
import { ThemeProvider, Flex } from '@knockrentals/knock-shared-web-v2';
import { getLeasingTeamRenderValue, getManagerRenderValue } from './utils';
import { TRANSFER_TARGET_FIELD_NAMES } from './constants';

const { COMPANY, LEASING_TEAM, MANAGER } = TRANSFER_TARGET_FIELD_NAMES;

const TransferTargetSelect = ({
  companies,
  company,
  handleCompanyOnChange,
  handleLeasingTeamOnChange,
  handleOnFocus,
  handleManagerOnChange,
  leasingTeam,
  leasingTeams,
  manager,
  managers,
  propertyOwner,
  validationErrorMessages,
}) => (
  <ThemeProvider>
    <Flex flexDirection="column" gap={16}>
      <Autocomplete
        disableClearable
        getOptionLabel={(option) => option.companyName ?? ''}
        getOptionSelected={(option, val) => option.companyId === val.companyId}
        onChange={handleCompanyOnChange}
        options={companies}
        renderInput={(params) => (
          <TextField
            {...params}
            error={Boolean(validationErrorMessages[COMPANY])}
            helperText={validationErrorMessages[COMPANY]}
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...params.inputProps, name: COMPANY }}
            label="Company"
            onFocus={handleOnFocus(COMPANY)}
            placeholder="Select a company"
            variant="outlined"
          />
        )}
        value={company}
      />

      <Flex flexDirection="column">
        <TextField
          disabled={!leasingTeams?.length}
          error={Boolean(validationErrorMessages[LEASING_TEAM])}
          fullWidth
          id="leasing-team-select"
          InputLabelProps={{ shrink: leasingTeams !== null }}
          inputProps={{ name: LEASING_TEAM }}
          label="Leasing Team"
          onChange={(e) => handleLeasingTeamOnChange(e.target.value)}
          onFocus={handleOnFocus(LEASING_TEAM)}
          select
          SelectProps={{
            displayEmpty: true,
            renderValue: getLeasingTeamRenderValue(leasingTeams),
          }}
          value={leasingTeam.leasingTeamId ? leasingTeam : ''}
          variant="outlined"
        >
          {(leasingTeams || []).map((option) => (
            <MenuItem key={option.leasingTeamId} value={option}>
              {option.leasingTeamName}
            </MenuItem>
          ))}
        </TextField>

        {validationErrorMessages[LEASING_TEAM] && (
          <FormHelperText error>
            {validationErrorMessages[LEASING_TEAM]}
          </FormHelperText>
        )}
      </Flex>

      <Flex flexDirection="column">
        <TextField
          disabled={!managers?.length}
          error={Boolean(validationErrorMessages[MANAGER])}
          fullWidth
          id="manager-select"
          InputLabelProps={{ shrink: managers !== null }}
          inputProps={{ name: MANAGER }}
          label="New Property Owner"
          onChange={(e) => handleManagerOnChange(e.target.value)}
          onFocus={handleOnFocus(MANAGER)}
          select
          SelectProps={{
            displayEmpty: true,
            renderValue: getManagerRenderValue(managers),
          }}
          value={manager.value ? manager : ''}
          variant="outlined"
        >
          {(managers || []).map((option) => (
            <MenuItem
              disabled={option.managerId === propertyOwner}
              key={option.value}
              value={option}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {validationErrorMessages[MANAGER] && (
          <FormHelperText error>
            {validationErrorMessages[MANAGER]}
          </FormHelperText>
        )}
      </Flex>
    </Flex>
  </ThemeProvider>
);

TransferTargetSelect.propTypes = {
  companies: PropTypes.arrayOf(
    PropTypes.shape({
      companyId: PropTypes.number,
      companyName: PropTypes.string,
    }),
  ),
  company: PropTypes.shape({
    companyId: PropTypes.number,
    companyName: PropTypes.string,
  }),
  handleCompanyOnChange: PropTypes.func,
  handleLeasingTeamOnChange: PropTypes.func,
  handleOnFocus: PropTypes.func,
  handleManagerOnChange: PropTypes.func,
  leasingTeam: PropTypes.shape({
    leasingTeamId: PropTypes.number,
    leasingTeamName: PropTypes.string,
    leasingTeamPublicId: PropTypes.string,
  }),
  leasingTeams: PropTypes.arrayOf(
    PropTypes.shape({
      leasingTeamId: PropTypes.number,
      leasingTeamName: PropTypes.string,
      leasingTeamPublicId: PropTypes.string,
    }),
  ),
  manager: PropTypes.shape({
    label: PropTypes.string,
    managerId: PropTypes.number,
    value: PropTypes.string,
  }),
  managers: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      managerId: PropTypes.number,
      value: PropTypes.string,
    }),
  ),
  propertyOwner: PropTypes.number,
  validationErrorMessages: PropTypes.shape({
    company: PropTypes.string,
    leasingTeam: PropTypes.string,
    manager: PropTypes.string,
  }),
};

export default TransferTargetSelect;
